import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { SessionStorageService } from '../../services/session-storage.service';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { ContainerFacilityService } from '../container-facility.service';
import {
  ContainerReferenceSuggestion,
  ContainerReferenceType,
  ContainerTrackingService
} from '../container-tracking.service';
import { ContainerFacility } from '../models/container-facility.model';
import {
  ContainerJourneyPlanItemType,
  ContainerJourneyTemplateItem,
  ContainerJourneyTemplateView
} from '../models/container-journey-plan.model';

@Component({
  selector: 'app-container-case-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    BreadcrumbModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    LazyLocationPickerDirective,
    TagModule,
    TextareaModule,
    ToastModule,
    PageDataPanelComponent,
    PageFormSectionComponent,
    PageHeroComponent,
    PageShellComponent,
    CommercialPartyPickerComponent
  ],
  providers: [MessageService],
  templateUrl: './container-case-editor.component.html',
  styleUrls: ['./container-case-editor.component.css']
})
export class ContainerCaseEditorComponent implements OnInit {
  commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
  form!: FormGroup;
  stepForm!: FormGroup;
  isSubmitting = false;
  companyId = 0;
  locations: Location[] = [];
  facilities: ContainerFacility[] = [];
  journeyProfiles: ContainerJourneyTemplateView[] = [];
  journeyBlueprint: ContainerJourneyTemplateItem[] = [];
  stepDialogVisible = false;
  editingStepIndex: number | null = null;
  referenceSuggestions: Record<ContainerReferenceType, ContainerReferenceSuggestion[]> = {
    BOOKING: [],
    BILL_OF_LADING: [],
    HOUSE_BILL: [],
    CUSTOMS_DECLARATION: []
  };

  readonly maximumContainers = 500;
  readonly equipmentTypeOptions = [
    { label: '20GP', value: '20GP' },
    { label: '40GP', value: '40GP' },
    { label: '40HC', value: '40HC' },
    { label: '45HC', value: '45HC' },
    { label: '20RF', value: '20RF' },
    { label: '40RF', value: '40RF' },
    { label: 'Tank', value: 'TANK' },
    { label: 'Flat Rack', value: 'FLAT_RACK' },
    { label: 'Open Top', value: 'OPEN_TOP' }
  ];
  readonly stepTypeOptions = [
    { label: 'Transport', value: 'TRANSPORT' },
    { label: 'Storage / Dwell', value: 'STORAGE' }
  ];
  readonly transportModeOptions = [
    { label: 'Road Move', value: 'TRUCK' },
    { label: 'Customer Delivery', value: 'CUSTOMER_DELIVERY' },
    { label: 'Terminal Transfer', value: 'TERMINAL_TRANSFER' },
    { label: 'Yard Transfer', value: 'YARD_TRANSFER' },
    { label: 'Empty Return', value: 'EMPTY_RETURN' }
  ];
  readonly priorityOptions = [
    { label: 'Low', value: 'LOW' },
    { label: 'Normal', value: 'NORMAL' },
    { label: 'High', value: 'HIGH' },
    { label: 'Urgent', value: 'URGENT' }
  ];
  readonly storageReasonOptions = [
    { label: 'Import terminal dwell', value: 'IMPORT_TERMINAL' },
    { label: 'Export terminal dwell', value: 'EXPORT_TERMINAL' },
    { label: 'Stuffing', value: 'STUFFING' },
    { label: 'De-stuffing / stripping', value: 'DESTUFFING' },
    { label: 'Transshipment', value: 'TRANSSHIPMENT' },
    { label: 'Empty release', value: 'EMPTY_RELEASE' },
    { label: 'Empty return', value: 'EMPTY_RETURN' },
    { label: 'Empty repositioning', value: 'EMPTY_REPOSITIONING' }
  ];

  breadcrumbItems: MenuItem[] = [
    { label: 'Container Tracking', routerLink: '/container-tracking/cases' },
    { label: 'Cases', routerLink: '/container-tracking/cases' },
    { label: 'Booking Intake' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private sessionStorageService: SessionStorageService,
    private containerTrackingService: ContainerTrackingService,
    private locationService: LocationService,
    private facilityService: ContainerFacilityService
  ) {}

  ngOnInit(): void {
    this.companyId = this.sessionStorageService.getCompany()?.id || 0;
    this.initForms();
    this.commercialPartyReference = {
      source: 'CRM',
      crmClientId: this.form.get('crmClientId')?.value
    };
    this.resizeContainerRows(1);
    this.form.get('numberOfContainers')?.valueChanges.subscribe(value => {
      const count = Math.min(this.maximumContainers, Math.max(1, Math.trunc(Number(value) || 1)));
      this.resizeContainerRows(count);
    });
    this.loadMasterData();
  }

  get containers(): FormArray {
    return this.form.get('containers') as FormArray;
  }

  submit(): void {
    if (!this.referenceReady() || !this.commercialReady() || this.form.invalid || !this.companyId || !this.journeyBlueprint.length) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Complete booking intake',
        detail: 'Select a customer and equipment journey profile, then enter a booking or bill of lading reference.'
      });
      return;
    }

    const customer = this.sessionStorageService.getCustomer();
    const value = this.form.getRawValue();
    const profile = this.selectedProfile();
    this.isSubmitting = true;
    this.containerTrackingService.createBookingIntake({
      owningCompanyId: this.companyId,
      crmClientId: value.crmClientId || undefined,
      crmAccountId: value.crmAccountId || undefined,
      crmClientContactId: value.crmClientContactId || undefined,
      customerCompanyId: value.customerCompanyId || undefined,
      customerContactId: value.customerContactId || undefined,
      bookingNumber: this.clean(value.bookingNumber),
      billOfLadingNumber: this.clean(value.billOfLadingNumber),
      houseBillNumber: this.clean(value.houseBillNumber),
      customsDeclarationNumber: this.clean(value.customsDeclarationNumber),
      numberOfContainers: Number(value.numberOfContainers),
      defaultIsoEquipmentType: value.defaultIsoEquipmentType || undefined,
      equipmentJourneyProfileId: Number(value.equipmentJourneyProfileId),
      equipmentJourneyProfileCode: profile?.template?.templateCode,
      plannedJourneyStartAt: value.plannedJourneyStartAt,
      journeyNotes: this.clean(value.journeyNotes),
      createdBy: customer?.id,
      containers: value.containers.map((item: any) => ({
        containerNumber: this.clean(item.containerNumber),
        isoEquipmentType: item.isoEquipmentType || value.defaultIsoEquipmentType || undefined,
        sealNumber: this.clean(item.sealNumber)
      })),
      journeyBlueprint: this.normalizedBlueprint()
    }).subscribe({
      next: result => {
        this.isSubmitting = false;
        const count = result.containerCases?.length || value.numberOfContainers;
        this.messageService.add({
          severity: 'success',
          summary: `${count} container cases created`,
          detail: `${result.existingProjectReused ? 'Reused' : 'Created'} logistics project ${result.logisticsProjectNumber || ''}.`
        });
        const firstCaseId = result.containerCases?.[0]?.id;
        if (count === 1 && firstCaseId) {
          this.router.navigate(['/container-tracking/cases/view', firstCaseId], { queryParams: { tab: 'journey-plan' } });
        } else {
          this.router.navigate(['/container-tracking/cases'], {
            queryParams: { bookingNumber: value.bookingNumber || undefined, billOfLadingNumber: value.billOfLadingNumber || undefined }
          });
        }
      },
      error: error => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Booking intake failed',
          detail: error?.error?.message || error?.error?.detail || 'Could not create the booking project and container cases.'
        });
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/container-tracking/cases']);
  }

  onCommercialPartyChange(reference: CommercialPartyReference): void {
    this.commercialPartyReference = reference;
    this.form.patchValue({
      crmClientId: reference.crmClientId ?? null,
      crmAccountId: reference.crmAccountId ?? null,
      crmClientContactId: reference.crmContactId ?? null,
      customerCompanyId: reference.legacyCompanyId ?? null,
      customerContactId: reference.legacyCustomerId ?? null
    });
  }

  searchReferences(referenceType: ContainerReferenceType, query: string): void {
    if (!this.companyId) {
      this.referenceSuggestions[referenceType] = [];
      return;
    }
    this.containerTrackingService.findReferenceSuggestions(this.companyId, referenceType, query || '').subscribe({
      next: suggestions => this.referenceSuggestions[referenceType] = suggestions || [],
      error: () => this.referenceSuggestions[referenceType] = []
    });
  }

  onReferenceSelected(suggestion: ContainerReferenceSuggestion): void {
    if (!suggestion?.value) {
      return;
    }
    const selectedControl = this.referenceControlName(suggestion.referenceType);
    const patch: Record<string, string> = { [selectedControl]: suggestion.value };
    this.patchReferenceWhenEmpty(patch, 'bookingNumber', suggestion.bookingNumber);
    this.patchReferenceWhenEmpty(patch, 'billOfLadingNumber', suggestion.billOfLadingNumber);
    this.patchReferenceWhenEmpty(patch, 'houseBillNumber', suggestion.houseBillNumber);
    this.patchReferenceWhenEmpty(patch, 'customsDeclarationNumber', suggestion.customsDeclarationNumber);
    this.form.patchValue(patch);
  }

  onProfileChange(profileId: number | null): void {
    const selected = this.journeyProfiles.find(profile => profile.template?.id === Number(profileId));
    this.journeyBlueprint = (selected?.items || []).map(item => ({ ...item }));
    this.resequenceBlueprint();
  }

  openStepDialog(type: ContainerJourneyPlanItemType, index: number | null = null): void {
    this.editingStepIndex = index;
    const existing = index == null ? null : this.journeyBlueprint[index];
    const previous = index == null ? this.journeyBlueprint[this.journeyBlueprint.length - 1] : this.journeyBlueprint[index - 1];
    this.stepForm.reset({
      itemType: existing?.itemType || type,
      stepName: existing?.stepName || '',
      transportMode: existing?.transportMode || 'TRUCK',
      priority: existing?.priority || 'NORMAL',
      originLocationId: existing?.originLocationId || null,
      destinationLocationId: existing?.destinationLocationId || null,
      facilityId: existing?.facilityId || null,
      storageReason: existing?.storageReason || 'IMPORT_TERMINAL',
      startOffsetHours: existing?.startOffsetHours ?? this.nextStartOffset(previous),
      plannedDurationHours: existing?.plannedDurationHours ?? 8,
      remarks: existing?.remarks || ''
    });
    this.stepDialogVisible = true;
  }

  saveStep(): void {
    if (this.stepForm.invalid) {
      this.stepForm.markAllAsTouched();
      return;
    }
    const value = this.stepForm.getRawValue();
    const isTransport = value.itemType === 'TRANSPORT';
    const facility = this.facilities.find(item => item.id === Number(value.facilityId));
    const origin = this.locations.find(item => item.id === Number(value.originLocationId));
    const destination = this.locations.find(item => item.id === Number(value.destinationLocationId));
    const item: ContainerJourneyTemplateItem = {
      sequenceNo: (this.editingStepIndex ?? this.journeyBlueprint.length) + 1,
      itemType: value.itemType,
      stepName: this.clean(value.stepName) || (isTransport ? 'Transport step' : 'Storage step'),
      startOffsetHours: Number(value.startOffsetHours) || 0,
      plannedDurationHours: Number(value.plannedDurationHours) || 0,
      priority: isTransport ? value.priority : undefined,
      transportMode: isTransport ? value.transportMode : undefined,
      originLocationId: isTransport ? value.originLocationId : undefined,
      originLocationText: isTransport ? this.locationLabel(origin) : undefined,
      destinationLocationId: isTransport ? value.destinationLocationId : undefined,
      destinationLocationText: isTransport ? this.locationLabel(destination) : undefined,
      facilityId: !isTransport ? value.facilityId : undefined,
      facilityName: !isTransport ? facility?.facilityName : undefined,
      facilityType: !isTransport ? facility?.facilityType : undefined,
      locationId: !isTransport ? facility?.locationId : undefined,
      locationText: !isTransport ? facility?.facilityName : undefined,
      storageControlMode: !isTransport ? (facility?.operationMode === 'REFERENCE_ONLY' ? 'REFERENCE_ONLY' : 'MANAGED') : undefined,
      operatorCrmClientId: !isTransport ? facility?.operatorCrmClientId : undefined,
      storageReason: !isTransport ? value.storageReason : undefined,
      remarks: this.clean(value.remarks)
    };
    if (this.editingStepIndex == null) {
      this.journeyBlueprint = [...this.journeyBlueprint, item];
    } else {
      this.journeyBlueprint = this.journeyBlueprint.map((current, index) => index === this.editingStepIndex ? item : current);
    }
    this.resequenceBlueprint();
    this.stepDialogVisible = false;
  }

  deleteStep(index: number): void {
    this.journeyBlueprint = this.journeyBlueprint.filter((_, current) => current !== index);
    this.resequenceBlueprint();
  }

  selectedProfile(): ContainerJourneyTemplateView | undefined {
    const id = Number(this.form.get('equipmentJourneyProfileId')?.value);
    return this.journeyProfiles.find(profile => profile.template?.id === id);
  }

  profileLabel(profile: ContainerJourneyTemplateView): string {
    return [profile.template?.templateName, profile.template?.templateCode].filter(Boolean).join(' | ');
  }

  stepLocation(item: ContainerJourneyTemplateItem): string {
    if (item.itemType === 'STORAGE') {
      return item.facilityName || item.locationText || this.facilityTypeLabel(item.facilityType) || 'Facility to be assigned';
    }
    return `${item.originLocationText || 'Origin to be assigned'} -> ${item.destinationLocationText || 'Destination to be assigned'}`;
  }

  stepControl(item: ContainerJourneyTemplateItem): string {
    return item.itemType === 'TRANSPORT'
      ? `${this.readable(item.transportMode)} | ${item.priority || 'NORMAL'}`
      : `${this.readable(item.storageControlMode || 'MANAGED')} | ${this.readable(item.storageReason)}`;
  }

  referenceReady(): boolean {
    return !!(this.clean(this.form.get('bookingNumber')?.value) || this.clean(this.form.get('billOfLadingNumber')?.value));
  }

  commercialReady(): boolean {
    return !!(this.form.get('crmClientId')?.value || this.form.get('customerCompanyId')?.value || this.form.get('customerContactId')?.value);
  }

  manifestReady(): boolean {
    const count = Number(this.form.get('numberOfContainers')?.value);
    return count > 0 && count <= this.maximumContainers && this.containers.length === count;
  }

  profileReady(): boolean {
    return !!this.form.get('equipmentJourneyProfileId')?.value && this.journeyBlueprint.length > 0;
  }

  readinessScore(): number {
    const checks = [this.referenceReady(), this.commercialReady(), this.manifestReady(), this.profileReady()];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }

  unresolvedStepCount(): number {
    return this.journeyBlueprint.filter(item => item.itemType === 'TRANSPORT'
      ? !item.originLocationId || !item.destinationLocationId
      : !item.facilityId).length;
  }

  locationLabel(location?: Location | null): string | undefined {
    return location?.name || undefined;
  }

  private initForms(): void {
    const query = this.route.snapshot.queryParamMap;
    this.form = this.fb.group({
      crmClientId: [Number(query.get('crmClientId') || '') || null],
      crmAccountId: [null],
      crmClientContactId: [null],
      customerCompanyId: [null],
      customerContactId: [null],
      bookingNumber: [query.get('bookingNumber') || ''],
      billOfLadingNumber: [query.get('billOfLadingNumber') || ''],
      houseBillNumber: [''],
      customsDeclarationNumber: [''],
      numberOfContainers: [1, [Validators.required, Validators.min(1), Validators.max(this.maximumContainers)]],
      defaultIsoEquipmentType: ['40HC'],
      equipmentJourneyProfileId: [null, Validators.required],
      plannedJourneyStartAt: [new Date(), Validators.required],
      journeyNotes: [''],
      containers: this.fb.array([])
    });
    this.stepForm = this.fb.group({
      itemType: ['TRANSPORT', Validators.required],
      stepName: ['', Validators.required],
      transportMode: ['TRUCK'],
      priority: ['NORMAL'],
      originLocationId: [null],
      destinationLocationId: [null],
      facilityId: [null],
      storageReason: ['IMPORT_TERMINAL'],
      startOffsetHours: [0, [Validators.required, Validators.min(0)]],
      plannedDurationHours: [8, [Validators.required, Validators.min(0)]],
      remarks: ['']
    });
  }

  private referenceControlName(referenceType: ContainerReferenceType): string {
    switch (referenceType) {
      case 'BOOKING': return 'bookingNumber';
      case 'BILL_OF_LADING': return 'billOfLadingNumber';
      case 'HOUSE_BILL': return 'houseBillNumber';
      case 'CUSTOMS_DECLARATION': return 'customsDeclarationNumber';
    }
  }

  private patchReferenceWhenEmpty(patch: Record<string, string>, controlName: string, value?: string | null): void {
    if (value && !this.clean(this.form.get(controlName)?.value)) {
      patch[controlName] = value;
    }
  }

  private loadMasterData(): void {
    if (!this.companyId) {
      return;
    }
    forkJoin({
      profiles: this.containerTrackingService.findJourneyTemplates(this.companyId),
      locations: this.locationService.getAllComboData('', 0, 25),
      facilities: this.facilityService.findAll(new HttpParams()
        .set('owningCompanyId', String(this.companyId))
        .set('active', 'true')
        .set('size', '500'))
    }).subscribe({
      next: result => {
        this.journeyProfiles = result.profiles || [];
        this.locations = result.locations.items || [];
        this.facilities = result.facilities.content || [];
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Setup data unavailable',
        detail: error?.error?.message || 'Could not load equipment journey profiles, locations, or facilities.'
      })
    });
  }

  private resizeContainerRows(count: number): void {
    while (this.containers.length < count) {
      this.containers.push(this.fb.group({
        containerNumber: [''],
        isoEquipmentType: [''],
        sealNumber: ['']
      }));
    }
    while (this.containers.length > count) {
      this.containers.removeAt(this.containers.length - 1);
    }
  }

  private resequenceBlueprint(): void {
    this.journeyBlueprint = this.journeyBlueprint.map((item, index) => ({
      ...item,
      sequenceNo: index + 1,
      predecessorSequenceNo: index > 0 ? index : undefined
    }));
  }

  private normalizedBlueprint(): ContainerJourneyTemplateItem[] {
    return this.journeyBlueprint.map((item, index) => ({
      ...item,
      sequenceNo: index + 1,
      predecessorSequenceNo: index > 0 ? index : undefined,
      sourceStorageSequenceNo: undefined,
      destinationStorageSequenceNo: undefined
    }));
  }

  private nextStartOffset(previous?: ContainerJourneyTemplateItem): number {
    if (!previous) {
      return 0;
    }
    return Number(previous.startOffsetHours || 0) + Number(previous.plannedDurationHours || 0);
  }

  private clean(value: unknown): string | undefined {
    const text = String(value ?? '').trim();
    return text || undefined;
  }

  private readable(value?: string | null): string {
    return value ? value.replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase()) : '-';
  }

  private facilityTypeLabel(value?: string | null): string | undefined {
    return value ? this.readable(value) : undefined;
  }
}
